import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { showToast } from '@/components/ui/sonner';
import { handleError } from '@/lib/utils';
import { getRoutes } from '@/routes/routePaths';
import { authState } from '@/vars/auth';
import { useMutation, useQuery } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { z } from 'zod';
import { CREATE_ASSESSMENT } from '../GraphQl/mutation';
import { GET_ASSESSMENT } from '../GraphQl/queries';

type TrainerFeedbackFormProps = {
  traineeId: string;
  updateProgress: (progress: number) => void;
};

const trainerFeedbackSchema = z.object({
  overallRating: z.number().min(0, 'Required').max(10, 'Max is 10'),
  mobility: z.number().min(0).max(10),
  fitnessLevel: z.number().min(0).max(10),
  flexibility: z.number().min(0).max(10),
  strength: z.number().min(0).max(10),
  strengthEndurance: z.number().min(0).max(10),
  familiarityWithTools: z.number().min(0).max(10),
});

type TrainerFeedbackFormData = z.infer<typeof trainerFeedbackSchema>;

export const createAssessmentData = (data: TrainerFeedbackFormData) => ({
  title1: 'Rate the client on a scale of 1 to 10',
  question1: '(1 = very low, 10 = very high):',
  answer1: data?.overallRating || 0,
  title2: 'Mobility',
  question2: 'Observe joint movement and ease of motion.',
  answer2: data?.mobility || 0,
  title3: 'Fitness Level',
  question3: 'Gauge overall fitness through conversation and initial activity.',
  answer3: data?.fitnessLevel || 0,
  title4: 'Flexibility',
  question4: 'Check range of motion in key muscle groups.',
  answer4: data?.flexibility || 0,
  title5: 'Strength',
  question5: 'Assess strength through basic movements (e.g., squats, push-ups).',
  answer5: data?.strength || 0,
  title6: 'Strength Endurance',
  question6: 'Evaluate how long they can maintain effort during activities.',
  answer6: data?.strengthEndurance || 0,
  title7: 'Familiarity with Fitness Tools',
  question7: 'Discuss their experience with various equipment and exercises.',
  answer7: data?.familiarityWithTools || 0,
});

const getDefaultValues = (assessmentData: any): TrainerFeedbackFormData => {
  const firstAssessment = assessmentData?.getAllAssessment?.[0]?.assessment;
  return {
    overallRating: firstAssessment?.answer1 ?? 0,
    mobility: firstAssessment?.answer2 ?? 0,
    fitnessLevel: firstAssessment?.answer3 ?? 0,
    flexibility: firstAssessment?.answer4 ?? 0,
    strength: firstAssessment?.answer5 ?? 0,
    strengthEndurance: firstAssessment?.answer6 ?? 0,
    familiarityWithTools: firstAssessment?.answer7 ?? 0,
  };
};

const trainerFeedbackForm = ({ traineeId, updateProgress }: TrainerFeedbackFormProps) => {
  const { id } = useParams();
  const { userDetails } = authState();
  const navigate = useNavigate();

  const [createAssessment, { loading: assessmentLoading }] = useMutation(CREATE_ASSESSMENT, {
    onCompleted: () => {
      updateProgress(2); // Move to next step on success
    },
    onError: handleError,
  });

  const { data: assessmentData } = useQuery(GET_ASSESSMENT, {
    variables: {
      filter: {
        sessionId: id,
      },
    },
    fetchPolicy: 'network-only',
  });

  // console.log(assessmentData);

  const trainerOnSubmit = async (data: TrainerFeedbackFormData) => {
    // Check if all values are 0
    const allZero =
      data.overallRating === 0 &&
      data.mobility === 0 &&
      data.fitnessLevel === 0 &&
      data.flexibility === 0 &&
      data.strength === 0 &&
      data.strengthEndurance === 0 &&
      data.familiarityWithTools === 0;
    if (allZero) {
      showToast({
        variant: 'error',
        message: 'Please fill out at least one field before submitting.',
      });
      return;
    } else {
      if (id && !assessmentLoading) {
        await createAssessment({
          variables: {
            data: {
              sessionId: id,
              assessment: createAssessmentData(data),
              trainerId: userDetails?._id || '',
              traineeId: traineeId || '',
            },
          },
        });
      }
    }
  };

  const redirectToHome = () => {
    navigate(getRoutes.Sessions, { replace: true });
  };

  // Use different form for trainer/coach
  const {
    control: trainerControl,
    handleSubmit: trainerFormSubmit,
    reset,
  } = useForm<TrainerFeedbackFormData>({
    resolver: zodResolver(trainerFeedbackSchema),
    defaultValues: getDefaultValues(assessmentData),
  });

  useEffect(() => {
    if ((assessmentData?.getAllAssessment?.length ?? 0) > 0) {
      reset(getDefaultValues(assessmentData));
    }
  }, [assessmentData, reset]);

  return (
    <form onSubmit={trainerFormSubmit(trainerOnSubmit)} className='flex flex-col gap-5'>
      <div className='w-full flex justify-between items-center gap-3'>
        <div className='flex flex-col mt-4 gap-2'>
          <h2>Initial Assessment</h2>
          <span className='font-medium text-[14px] leading-[140%] tracking-[0%] text-[#A8ADB9]'>
            To evaluate new clients using a 1 to 10 scale for an accurate understanding of their
            physical status and preferences.
          </span>
        </div>
        <Button onClick={redirectToHome} id='feedback-close-btn' size='icon' variant='ghost'>
          <X className='w-6 h-6' />
        </Button>
      </div>
      {/* Slider fields */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Rate the client on a scale of 1 to 10</Label>
        <span className='text-xs  mb-2'>(1 = very low, 10 = very high)</span>
        <Controller
          name='overallRating'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Mobility */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Mobility</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Observe joint movement and ease of motion.
        </span>
        <Controller
          name='mobility'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Fitness Level */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Fitness Level</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Gauge overall fitness through conversation and initial activity.
        </span>
        <Controller
          name='fitnessLevel'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Flexibility */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Flexibility</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Check range of motion in key muscle groups.
        </span>
        <Controller
          name='flexibility'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Strength */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Strength</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Assess strength through basic movements (e.g., squats, push-ups).
        </span>
        <Controller
          name='strength'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Strength Endurance */}
      <div className='flex flex-col gap-4 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Strength Endurance</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Evaluate how long they can maintain effort during activities.
        </span>
        <Controller
          name='strengthEndurance'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      {/* Familiarity with Fitness Tools */}
      <div className='flex flex-col gap-1 bg-bgPrimary rounded-xl p-4'>
        <Label className='text-xl font-semibold'>Familiarity with Fitness Tools</Label>
        <span className='text-xs text-[#C5C8D0] mb-2'>
          Discuss their experience with various equipment and exercises.
        </span>
        <Controller
          name='familiarityWithTools'
          control={trainerControl}
          render={({ field }) => (
            <div
              className='flex items-center gap-4 py-4 px-[18px] rounded-[20px]'
              style={{
                background:
                  'linear-gradient(154.36deg, rgba(47, 47, 47, 0.44) 11.3%, rgba(47, 47, 47, 0.44) 95.38%)',
              }}
            >
              <Slider
                min={0}
                max={10}
                step={1}
                value={[field.value]}
                onValueChange={([val]: number[]) => field.onChange(val)}
                className='flex-1'
              />
              <span className='w-8 text-center font-bold text-lg text-[#E9CC7C]'>
                {String(field.value).padStart(2, '0')}
              </span>
            </div>
          )}
        />
      </div>
      <div className='flex gap-4'>
        <Button type='submit' loading={assessmentLoading} className='w-full' id='feedback-continue'>
          Continue
        </Button>
      </div>
    </form>
  );
};

export default trainerFeedbackForm;
